import React, { Component } from 'react';
import { Box, Text } from 'grommet';
import { Alert, Button, Input, Select, Table, Tag } from 'antd';
import { OPS_DATA_URL } from '../config';

const { Option } = Select;
const { Search } = Input;

const EXCLUDED_NAMESPACES = ['kube-system', 'cert-manager', 'monitoring'];

const getStatusColor = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'deployed') return 'green';
  if (normalized === 'failed') return 'red';
  if (normalized === 'pending-install' || normalized === 'pending-upgrade') return 'orange';
  if (normalized === 'superseded') return 'default';
  return 'default';
};

class AviatorVersions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: '',
      rows: [],
      namespaceFilter: 'ALL',
      statusFilter: 'ALL',
      searchText: '',
    };
  }

  componentDidMount() {
    this.loadAviatorVersions();
  }

  loadAviatorVersions = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const res = await fetch(`${OPS_DATA_URL}/aviator-versions`, { method: 'GET' });
      if (!res.ok) {
        this.setState({ loading: false, error: 'Failed to load aviator versions data.' });
        return;
      }

      const payload = await res.json();
      const rows = Array.isArray(payload.items) ? payload.items : [];
      this.setState({ loading: false, rows });
    } catch (error) {
      this.setState({ loading: false, error: 'Network error while loading aviator versions data.' });
    }
  }

  getUniqueValues = (key) => {
    const values = this.state.rows
      .map((row) => (row[key] || '').trim())
      .filter((value) => value.length > 0)
      .filter((value) => key !== 'namespace' || !EXCLUDED_NAMESPACES.includes(value));
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
  }

  getFilteredRows = () => {
    const {
      rows,
      namespaceFilter,
      statusFilter,
      searchText,
    } = this.state;

    const normalizedSearch = searchText.trim().toLowerCase();

    return rows.filter((row) => {
      if (namespaceFilter !== 'ALL' && row.namespace !== namespaceFilter) return false;
      if (statusFilter !== 'ALL' && row.status !== statusFilter) return false;

      if (normalizedSearch.length > 0) {
        const haystack = [row.name, row.namespace, row.chart, row.app_version, row.status]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }

      return true;
    });
  }

  resetFilters = () => {
    this.setState({
      namespaceFilter: 'ALL',
      statusFilter: 'ALL',
      searchText: '',
    });
  }

  render() {
    const {
      loading,
      error,
      namespaceFilter,
      statusFilter,
      searchText,
      rows,
    } = this.state;

    const namespaces = this.getUniqueValues('namespace');
    const statuses = this.getUniqueValues('status');
    const filteredRows = this.getFilteredRows();

    const columns = [
      {
        title: 'Release Name',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        sorter: (left, right) => (left.name || '').localeCompare(right.name || ''),
      },
      {
        title: 'Namespace',
        dataIndex: 'namespace',
        key: 'namespace',
        width: 140,
        sorter: (left, right) => (left.namespace || '').localeCompare(right.namespace || ''),
      },
      {
        title: 'Chart',
        dataIndex: 'chart',
        key: 'chart',
        width: 280,
        sorter: (left, right) => (left.chart || '').localeCompare(right.chart || ''),
      },
      {
        title: 'App Version',
        dataIndex: 'app_version',
        key: 'app_version',
        width: 180,
        render: (value) => value || '-',
        sorter: (left, right) => (left.app_version || '').localeCompare(right.app_version || ''),
      },
      {
        title: 'Revision',
        dataIndex: 'revision',
        key: 'revision',
        width: 90,
        sorter: (left, right) => Number(left.revision || 0) - Number(right.revision || 0),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (value) => <Tag color={getStatusColor(value)}>{value || 'N/A'}</Tag>,
        sorter: (left, right) => (left.status || '').localeCompare(right.status || ''),
      },
      {
        title: 'Updated',
        dataIndex: 'updated',
        key: 'updated',
        width: 260,
        render: (value) => value || '-',
      },
    ];

    return (
      <Box width='100%' gap='small'>
        <Box direction='row' justify='between' align='center' wrap gap='small'>
          <Text size='xlarge' weight='bold'>Aviator Farm App Versions</Text>
          <Button loading={loading} onClick={this.loadAviatorVersions}>Refresh</Button>
        </Box>

        {error ? <Alert type='error' message={error} showIcon /> : null}

        <Box direction='row' wrap gap='small' align='center'>
          <Select
            value={namespaceFilter}
            style={{ minWidth: 180 }}
            onChange={(value) => this.setState({ namespaceFilter: value })}
          >
            <Option value='ALL'>All Namespaces</Option>
            {namespaces.map((ns) => <Option key={ns} value={ns}>{ns}</Option>)}
          </Select>

          <Select
            value={statusFilter}
            style={{ minWidth: 160 }}
            onChange={(value) => this.setState({ statusFilter: value })}
          >
            <Option value='ALL'>All Statuses</Option>
            {statuses.map((st) => <Option key={st} value={st}>{st}</Option>)}
          </Select>

          <Search
            value={searchText}
            onChange={(event) => this.setState({ searchText: event.target.value })}
            placeholder='Search name, chart, version'
            allowClear
            style={{ minWidth: 260 }}
          />

          <Button onClick={this.resetFilters}>Reset</Button>
        </Box>

        <Text size='small' color='dark-5'>
          Showing {filteredRows.length} rows from {rows.length} total releases
        </Text>

        <Table
          rowKey='key'
          loading={loading}
          dataSource={filteredRows}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
          scroll={{ x: 1200 }}
          size='middle'
        />
      </Box>
    );
  }
}

export default AviatorVersions;
